import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from "react";
import type { ApolloClient } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import type { EntityAvatarPresenceStatus } from "@salestrak/ui";

import {
  GetPresencesDocument,
  PresenceChangedDocument,
  PresenceEntryFragmentDoc,
  PresenceHeartbeatDocument,
  type PresenceChangedSubscription,
  type PresenceSource,
  type PresenceStatus,
} from "#app/graphql/generated/graphql";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  useRegisterSubscriptions,
  type AnySubscriptionRequest,
  type SubscriptionRequest,
} from "#app/lib/subscriptions/subscription-provider";

export type { PresenceSource, PresenceStatus };

/** A member's live presence: their status and which surface it came from
 *  (server resolves web-trumps-mobile; MOBILE means phone-only). */
export interface PresenceEntry {
  status: PresenceStatus;
  source: PresenceSource;
}

// GraphQL enum name → the library dot's lowercase status union.
const PRESENCE_DOT_STATUS = {
  ONLINE: "online",
  AWAY: "away",
  OFFLINE: "offline",
} as const satisfies Record<PresenceStatus, EntityAvatarPresenceStatus>;

/** The one entry → Avatar `presence` prop mapping: status enum translation
 *  plus the phone-glyph rule (phone-only presence renders the smartphone
 *  glyph — unless offline, where the glyph would imply a live phone). */
export function presenceDotProps(entry: PresenceEntry): {
  status: EntityAvatarPresenceStatus;
  mobile: boolean;
} {
  return {
    status: PRESENCE_DOT_STATUS[entry.status],
    mobile: entry.source === "MOBILE" && entry.status !== "OFFLINE",
  };
}

// Heartbeat cadence. The server key TTL is 90s (PRESENCE_TTL_SECONDS in
// core's presence.service.ts) — 30s gives two missed beats of slack before
// the viewer reads as offline.
const HEARTBEAT_MS = 30_000;
// How often the loop re-evaluates online↔away so a flip is reported promptly
// (a flip sends immediately; steady state only sends on the heartbeat beat).
const EVALUATE_MS = 10_000;
// No pointer/key input for this long → away.
const IDLE_AWAY_MS = 5 * 60_000;
// Hysteresis on a hidden tab before it reads away. Every status flip costs a
// heartbeat mutation, a workspace-wide fan-out publish, and an avatar
// re-render on every teammate's client — so someone alt-tabbing to copy an
// address must not flicker every board in the workspace. Matches the
// heartbeat cadence, so a tab that stays hidden reports away on its next
// natural beat rather than an extra one.
const HIDDEN_AWAY_MS = 30_000;
// How long someone stays yellow: after this much CONTINUOUS away the tab
// stops heartbeating, so the server's 90s TTL lapses and its sweeper
// publishes OFFLINE — an abandoned-but-open tab reads grey, not
// yellow-forever. Any return to activity resumes beats (and ONLINE)
// immediately.
const AWAY_OFFLINE_AFTER_MS = 10 * 60_000;

// ── Sign-out fast path ──────────────────────────────────────────────────────
// The server treats an OFFLINE beat as "drop this channel NOW" — it deletes the
// presence key instead of letting PRESENCE_TTL_SECONDS lapse, and names
// sign-out as the case that fast path exists for (core's presence.service.ts
// `heartbeat`). Wiring it up has one ordering constraint: the beat has to leave
// while the viewer is still authenticated. PresenceProvider unmounts only
// AFTER signOut() has torn down auth state, so an unmount-time beat would race
// a token that's already gone — hence this registry, which signOut() awaits
// before it clears tokens.
//
// Multi-tab: the channel is keyed by auth user + source (not per tab), so
// signing out of one tab momentarily clears presence for another open tab;
// that tab's next heartbeat (≤30s) restores it. The beat is best-effort — a
// failed or slow one must never stall sign-out, and TTL expiry stays the
// fallback for tab closes, crashes, and network drops.
let offlineBeat: (() => Promise<unknown>) | null = null;

// Sign-out shouldn't visibly wait on presence bookkeeping. One round trip is
// all this needs; past that the TTL sweep handles it.
const OFFLINE_BEAT_TIMEOUT_MS = 1_000;

/** Beat OFFLINE so teammates see this viewer drop immediately rather than
 *  after the server's TTL. Resolves either way — never throws, never stalls
 *  sign-out past OFFLINE_BEAT_TIMEOUT_MS. No-op when no provider is mounted
 *  (signed out already, or no workspace member resolved). */
export async function beatPresenceOffline(): Promise<void> {
  const beat = offlineBeat;
  if (!beat) return;
  try {
    await Promise.race([
      beat(),
      new Promise((resolve) => setTimeout(resolve, OFFLINE_BEAT_TIMEOUT_MS)),
    ]);
  } catch {
    // Presence is eventually consistent by design; the sweeper still demotes
    // this viewer when the TTL lapses.
  }
}

// Live updates ride the PresenceChanged subscription: each event is one
// member's new status, written straight onto the normalized UserPresence
// entity so the GetPresences snapshot (and the store fed from it) updates in
// place. No polling loop.
const PRESENCE_SUBSCRIPTION: SubscriptionRequest<
  PresenceChangedSubscription,
  never
>[] = [
  {
    document: PresenceChangedDocument,
    onData: (client, data) => {
      const event = data.presenceChanged;
      if (!event) return;
      const cacheId = client.cache.identify({
        __typename: "UserPresence",
        userId: event.userId,
      });
      // identify() is undefined only for an object missing its key fields —
      // impossible for a server payload, but the narrowing is required.
      if (cacheId === undefined) return;
      client.cache.writeFragment({
        id: cacheId,
        fragment: PresenceEntryFragmentDoc,
        data: { ...event, __typename: "UserPresence" },
      });
      listInSnapshot(client, event.userId);
    },
  },
];

/**
 * Make sure the `presences` snapshot list carries this member.
 *
 * The fragment write above updates a row that is ALREADY listed, but it can't
 * add one — and the store is fed from the list, not from the normalized
 * entities. Someone who joined the workspace after the snapshot landed (an
 * invite accepted while this tab is open) would otherwise stay dot-less until
 * a reload, no matter how many events they publish.
 */
function listInSnapshot(client: ApolloClient, userId: string): void {
  client.cache.modify({
    fields: {
      presences(existing: any, { toReference, readField }: any) {
        const rows: readonly any[] = Array.isArray(existing) ? existing : [];
        if (rows.some((row) => readField("userId", row) === userId)) {
          return existing;
        }
        const ref = toReference({ __typename: "UserPresence", userId });
        return ref ? [...rows, ref] : existing;
      },
    },
  });
}

const NO_SUBSCRIPTIONS: AnySubscriptionRequest[] = [];

/**
 * Presence lives in this tiny external store rather than React state:
 * hundreds of avatars can be mounted at once on a board/table, and pushing a
 * new map through context would re-render every one of them (context
 * invalidation bypasses `React.memo`) each time any single member flips
 * status. The store notifies per-userId subscribers instead, so one member's
 * flip re-renders only that member's avatars. Entry objects keep their
 * identity while unchanged, so per-id snapshots stay referentially stable.
 */
class PresenceStore {
  private map = new Map<string, PresenceEntry>();
  private idListeners = new Map<string, Set<() => void>>();
  private mapListeners = new Set<() => void>();

  get(userId: string): PresenceEntry | undefined {
    return this.map.get(userId);
  }

  /** Current map — identity changes exactly when any entry changes. */
  getMap(): ReadonlyMap<string, PresenceEntry> {
    return this.map;
  }

  subscribe(userId: string, listener: () => void): () => void {
    let set = this.idListeners.get(userId);
    if (!set) {
      set = new Set();
      this.idListeners.set(userId, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
      if (set.size === 0) this.idListeners.delete(userId);
    };
  }

  subscribeMap(listener: () => void): () => void {
    this.mapListeners.add(listener);
    return () => {
      this.mapListeners.delete(listener);
    };
  }

  /** Replace state from a snapshot/cache emit, notifying only the userIds
   *  whose entry actually changed (plus whole-map subscribers). A no-change
   *  emit keeps the previous map identity and notifies no one. */
  update(
    presences: readonly {
      userId: string;
      status: PresenceStatus;
      source: PresenceSource;
    }[],
  ): void {
    const prev = this.map;
    const next = new Map<string, PresenceEntry>();
    const changed: string[] = [];
    for (const p of presences) {
      const before = prev.get(p.userId);
      if (before && before.status === p.status && before.source === p.source) {
        next.set(p.userId, before);
      } else {
        next.set(p.userId, { status: p.status, source: p.source });
        changed.push(p.userId);
      }
    }
    for (const id of prev.keys()) {
      if (!next.has(id)) changed.push(id);
    }
    if (changed.length === 0) return;
    this.map = next;
    for (const id of changed) {
      this.idListeners.get(id)?.forEach((listener) => listener());
    }
    this.mapListeners.forEach((listener) => listener());
  }
}

// Default store for trees without a provider — never updated, so every hook
// reads "unknown" and renders no indicator.
const EMPTY_STORE = new PresenceStore();

const PresenceStoreContext = createContext<PresenceStore>(EMPTY_STORE);

/** The viewer's current self-reported status: away once the tab has been
 *  hidden past HIDDEN_AWAY_MS or input has been idle past IDLE_AWAY_MS,
 *  online otherwise. `hiddenSince` is null while the tab is visible. */
function currentStatus(
  lastActivityAt: number,
  hiddenSince: number | null,
): PresenceStatus {
  const now = Date.now();
  if (hiddenSince !== null && now - hiddenSince >= HIDDEN_AWAY_MS) {
    return "AWAY";
  }
  return now - lastActivityAt > IDLE_AWAY_MS ? "AWAY" : "ONLINE";
}

/**
 * Live presence for the workspace: heartbeats the viewer's own status
 * (online, or away after 30s on a hidden tab / 5 min of no input; after 10
 * min of unbroken away the tab goes silent so the server demotes it to
 * offline), takes ONE GetPresences snapshot per workspace for its members,
 * then stays current from PresenceChanged subscription events — including
 * OFFLINE transitions, which the server's sweeper publishes when someone's
 * heartbeat TTL lapses, and members who joined after the snapshot.
 *
 * Consumers read per-member status via `usePresence(userId)` (the
 * workspace-scoped user id, i.e. `WorkspaceUser.userId`). It returns
 * `undefined` until the snapshot lands so avatars never flash an offline
 * dot for someone who's actually online — mirroring the discovery-indicator
 * "hidden until the server confirms" contract.
 */
export function PresenceProvider({ children }: { children: React.ReactNode }) {
  const { userId } = useWorkspaceData();

  const [heartbeat] = useMutation(PresenceHeartbeatDocument);

  // ── Viewer heartbeat ──────────────────────────────────────────────────────
  const lastActivityRef = useRef(Date.now());
  // When the tab was hidden; null while visible.
  const hiddenSinceRef = useRef<number | null>(
    typeof document !== "undefined" && document.hidden ? Date.now() : null,
  );
  // When the current unbroken away stretch began; null while online.
  const awayStartRef = useRef<number | null>(null);
  const lastSentRef = useRef<{ status: PresenceStatus | null; at: number }>({
    status: null,
    at: 0,
  });

  useEffect(() => {
    if (!userId) return;

    const markActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const send = (status: PresenceStatus) => {
      lastSentRef.current = { status, at: Date.now() };
      // Fire-and-forget: a dropped beat self-heals on the next one, and the
      // TTL turning the viewer offline is the correct degraded state.
      void heartbeat({ variables: { status } }).catch(() => {});
    };

    const evaluate = () => {
      const now = Date.now();
      const status = currentStatus(
        lastActivityRef.current,
        hiddenSinceRef.current,
      );
      if (status === "AWAY") {
        awayStartRef.current ??= now;
        // Deep away: yellow has run its course — go silent and let the
        // server TTL + sweeper demote this viewer to offline (grey stays
        // until they're active again).
        if (now - awayStartRef.current >= AWAY_OFFLINE_AFTER_MS) return;
      } else {
        awayStartRef.current = null;
      }
      const last = lastSentRef.current;
      if (status !== last.status || now - last.at >= HEARTBEAT_MS) {
        send(status);
      }
    };

    // Passive listeners: these fire constantly (mousemove) and only stamp a
    // ref — no state, no re-render.
    const activityEvents = [
      "pointerdown",
      "keydown",
      "mousemove",
      "wheel",
      "touchstart",
    ] as const;
    for (const event of activityEvents) {
      window.addEventListener(event, markActivity, { passive: true });
    }
    const onVisibility = () => {
      if (document.hidden) {
        // Only stamp when the hidden stretch begins, and DON'T report on the
        // way out — the evaluate loop publishes away once the stretch passes
        // HIDDEN_AWAY_MS, so a brief alt-tab sends nothing at all.
        hiddenSinceRef.current ??= Date.now();
        return;
      }
      hiddenSinceRef.current = null;
      markActivity();
      // Coming back is worth reporting immediately: a viewer already demoted
      // to away (or offline) should go green without waiting for the loop.
      evaluate();
    };
    document.addEventListener("visibilitychange", onVisibility);

    evaluate();
    const interval = setInterval(evaluate, EVALUATE_MS);

    // Hand signOut() a sender bound to this tree's authenticated mutation —
    // see the sign-out fast path above. Deliberately not routed through
    // `send()`: this is a terminal beat, not part of the online↔away loop.
    offlineBeat = () => heartbeat({ variables: { status: "OFFLINE" } });

    return () => {
      offlineBeat = null;
      clearInterval(interval);
      for (const event of activityEvents) {
        window.removeEventListener(event, markActivity);
      }
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userId, heartbeat]);

  // ── Member statuses ───────────────────────────────────────────────────────
  // Live updates over the subscription registry (skipped until a workspace
  // member is resolved); the snapshot below covers the ENTIRE workspace
  // roster (server-resolved — a client-built id list would be capped by the
  // members page size and silently drop everyone past it).
  useRegisterSubscriptions(userId ? PRESENCE_SUBSCRIPTION : NO_SUBSCRIPTIONS);

  const { data, refetch } = useQuery(GetPresencesDocument, {
    fetchPolicy: "cache-and-network",
    skip: !userId,
  });

  const storeRef = useRef<PresenceStore | null>(null);
  storeRef.current ??= new PresenceStore();
  const store = storeRef.current;

  useEffect(() => {
    if (data?.presences) store.update(data.presences);
  }, [data, store]);

  // The snapshot is workspace-scoped, and a workspace switch REUSES this
  // provider — so nothing re-runs GetPresences on its own: `clearStore()`
  // drops the cached row without refetching active queries, and the query
  // takes no variables that could change. Left alone, the store keeps serving
  // the previous workspace's user ids (which don't collide with the new
  // workspace's, so every avatar silently loses its dot until a reload).
  //
  // `userId` is the workspace-scoped member id, so it flips exactly when the
  // switch completes — after the new workspace header is active, which a
  // trigger on the active workspace id could not promise.
  const snapshotForRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!userId) return;
    const previous = snapshotForRef.current;
    snapshotForRef.current = userId;
    // First snapshot for this member: the query above is already fetching it.
    if (previous === undefined || previous === userId) return;
    // Drop the old roster now rather than showing its statuses against the
    // new workspace's members while the fresh snapshot is in flight.
    store.update([]);
    void refetch().catch(() => {});
  }, [userId, refetch, store]);

  return (
    <PresenceStoreContext.Provider value={store}>
      {children}
    </PresenceStoreContext.Provider>
  );
}

/**
 * Live presence for a workspace member, by workspace-scoped user id.
 * `undefined` while unknown (snapshot in flight, id not a workspace member,
 * or provider absent) — render no indicator in that case. `source: "MOBILE"`
 * means phone-only (the server resolves web-trumps-mobile).
 *
 * Subscribes to that ONE member's entry, so a presence event re-renders only
 * the avatars of the member it changed.
 */
export function usePresence(
  userId: string | null | undefined,
): PresenceEntry | undefined {
  const store = useContext(PresenceStoreContext);
  const subscribe = useCallback(
    (listener: () => void) =>
      userId ? store.subscribe(userId, listener) : () => {},
    [store, userId],
  );
  return useSyncExternalStore(subscribe, () =>
    userId ? store.get(userId) : undefined,
  );
}

/**
 * The whole presence map — for components deriving indicators for MANY
 * members in one memo (EntityGroupTag). Reference-stable across renders
 * that change nothing; re-renders the caller on ANY member's change, so
 * prefer `usePresence` for single-member reads.
 */
export function usePresenceMap(): ReadonlyMap<string, PresenceEntry> {
  const store = useContext(PresenceStoreContext);
  const subscribe = useCallback(
    (listener: () => void) => store.subscribeMap(listener),
    [store],
  );
  return useSyncExternalStore(subscribe, () => store.getMap());
}
